// Libs
import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { close as closeIcon } from '@ibinder/common-icons';
import { deepFinder } from '@ibinder/common-utils';

// Componenter
import {
  Tab,
  TabHeader,
  BorderBottom,
  iBinderMainTheme,
  ToolButton
} from '@ibinder/common-frontend';
import useWindowSize from 'Shared/hooks/useWindowSize';
import { AdminBinderContext } from './index';

// Utils

const TabsWrapper = styled.div`
  border-top-right-radius: 0.25rem;
  border-bottom-right-radius: 0.25rem;
  z-index: 20;
  max-height: calc(100vh - 8rem);
  /*max-height: calc(100vh - 8rem); */
  @media (max-width: ${(props) => props.theme.devices.sm}px) {
    height: 100%;
    border-radius: 0.5rem;
  }
`;
const ContentWrapper = styled.div`
  margin: 0.5rem 0.5rem 0 0;
  padding: 0 0.375rem 0.375rem 0;
  box-shadow: ${(p) => p.theme.shadows.primaryOpacity};
  border-top-right-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;
  overflow-y: auto;
  max-height: calc(100vh - 12.25rem);
`;
const Inner = styled.div`
  background: ${(props) => props.theme.colors.primary5};
  padding: 0 0 1rem 0;
  height: calc(100% - 1rem);
`;
const RECONTAINER = styled.div`
  display: ${(p) => (p.isParentToggled ? 'block' : 'none')};
  background: ${(p) => (p.isToggled ? '#F4F6F8' : 'transparent')};
  padding: ${(p) => (p.isToggled ? '6px' : '0px')};
  padding-left: 0;
  box-shadow: ${(p) =>
    p.isToggled ? `${p.theme.shadows.primaryOpacity}` : 'none'};
  box-sizing: border-box;
  margin: 2px 0px;
  min-width: 170px;
  cursor: default;
  border-top-right-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;
`;
const IndexTabHeader = styled(TabHeader)`
  position: sticky;
  top: 0;
  z-index: 100;
  margin-bottom: 1rem;
  p {
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;
const StyledTab = styled.div`
  display: ${(p) => (p.isToggled ? 'block' : 'none')};
  margin-bottom: 1px;
`;
const TabName = styled.span`
  cursor: pointer;
  width: fit-content;
  overflow: hidden;
  p {
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;
const TabWithNoChildren = styled.span`
  font-weight: 400;
  text-overflow: ellipsis;
  overflow: hidden;
`;
const BorderBottomNoMarginBottom = styled(BorderBottom)`
  margin-bottom: 0;
  overflow: hidden;
  align-items: flex-start;
  p {
    text-overflow: ellipsis;
    overflow: hidden;
  }
  /* TODO: Should BorderBottom have a color in the first place? */
  && {
    color: ${({ theme }) => theme.colors.discrete};
  }
  button {
    margin: -0.75rem -1rem -0.75rem 0;
  }
`;

const TreeStruct = ({
  data,
  type = undefined,
  toggleTabsMenu,
  binderSpread,
  activeTabId,
  toggled = true,
  name = null,
  id = null,
  isChildElement = false,
  isParentToggled = true,
  childLevel = 0,
  ...otherProps
}) => {
  const [isToggled, setIsToggled] = React.useState(toggled);

  const isActiveUrl = id === activeTabId;

  const windowSize = useWindowSize();
  const isMobile = windowSize.width <= iBinderMainTheme.devices.md;

  React.useEffect(() => {
    if (childLevel > 0) {
      const CURRENT_NODE_DATA = deepFinder(data, activeTabId, 'subTabs');
      if (id === activeTabId) {
        setIsToggled(true);
      } else {
        setIsToggled(!!CURRENT_NODE_DATA);
      }
    }
  }, [activeTabId]);

  const collapseHeader = (id) => {
    setIsToggled(!isToggled);
    if (isMobile) {
      toggleTabsMenu();
    }
  };

  return (
    <RECONTAINER
      isToggled={name && isToggled}
      isParentToggled={isParentToggled}
      {...otherProps}
    >
      {name && isChildElement && (
        <Link to={`/tab/${id}`} onClick={() => collapseHeader(id)}>
          {isToggled ? (
            <TabHeader isActive={isActiveUrl}>
              <TabName>
                <p>{name}</p>
              </TabName>
            </TabHeader>
          ) : (
            <Tab isActive={isActiveUrl}>
              <TabName>
                <p>{name}</p>
              </TabName>
            </Tab>
          )}
        </Link>
      )}

      {data.map((v, i) => {
        return v.subTabs?.length > 0 && isToggled ? (
          <TreeStruct
            type={v.type}
            data={v.subTabs.map((d) => ({ ...d, type: v.type }))}
            toggleTabsMenu={toggleTabsMenu}
            id={v.id}
            childLevel={childLevel + 1}
            key={i}
            name={v.name}
            toggled={v.id === activeTabId}
            {...{
              activeTabId
            }}
            isChildElement={true}
            binderSpread={binderSpread}
            isParentToggled={isParentToggled && isToggled}
          />
        ) : (
          <Link
            key={i}
            to={`/tab/${v.id}`}
            onClick={() => {
              isMobile ? toggleTabsMenu() : null;
            }}
          >
            <StyledTab
              isChildElement={isChildElement}
              isToggled={isToggled}
              key={i}
            >
              <Tab isActive={v.id === activeTabId}>
                {Array.isArray(v) ? null : (
                  <TabWithNoChildren>{v.name}</TabWithNoChildren>
                )}
              </Tab>
            </StyledTab>
          </Link>
        );
      })}
    </RECONTAINER>
  );
};

const TabContainer = ({ data, toggleTabsMenu, binderName, binderSpread }) => {
  const location = useLocation();
  const windowSize = useWindowSize();
  const isMobile = windowSize.width <= iBinderMainTheme.devices.md;

  const theBinderSpread = `/`;
  const routeBinderSpread = location.pathname === theBinderSpread;
  const { activeTabId } = React.useContext(AdminBinderContext);

  return (
    <TabsWrapper>
      <BorderBottomNoMarginBottom>
        <p>{binderName}</p>
        <ToolButton type="button" onClick={toggleTabsMenu}>
          {closeIcon}
        </ToolButton>
      </BorderBottomNoMarginBottom>
      <Inner>
        <ContentWrapper>
          <Link
            to={theBinderSpread}
            onClick={() => {
              isMobile && toggleTabsMenu();
            }}
          >
            <IndexTabHeader isActive={routeBinderSpread}>
              <TabName>
                <p>Home</p>
              </TabName>
            </IndexTabHeader>
          </Link>
          <TreeStruct
            data={data}
            binderSpread={binderSpread}
            toggleTabsMenu={toggleTabsMenu}
            activeTabId={activeTabId}
          />
        </ContentWrapper>
      </Inner>
    </TabsWrapper>
  );
};

export default TabContainer;
